import { useState, useRef, useEffect } from "react";
import classes from "./RightSide.module.css";
import { Link } from "react-router-dom";
import { useSubmitProviderLead, EMAIL_REGEX } from "../../hooks/useSubmitProviderLead";
import { trackLead } from "../../util/tracking";

/** Min. digits for optional phones (typical length after +49 for DE). */
const MIN_PHONE_DIGITS = 10;

const stripNonDigits = (value) => String(value ?? "").replace(/\D/g, "");

export const RightSide = () => {
  const [referrerContactType, setReferrerContactType] = useState(null);
  const [studioName, setStudioName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneDigits, setPhoneDigits] = useState("");
  const [referrerEmail, setReferrerEmail] = useState("");
  const [referrerPhoneDigits, setReferrerPhoneDigits] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showDatenschutzBox, setShowDatenschutzBox] = useState(false);
  const datenschutzRef = useRef(null);
  const formRef = useRef(null);
  const { submitLead, isSubmitting, error } = useSubmitProviderLead();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datenschutzRef.current && !datenschutzRef.current.contains(event.target)) {
        setShowDatenschutzBox(false);
      }
    };
    if (showDatenschutzBox) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDatenschutzBox]);

  const emailTrimmed = email.trim();
  const providerEmailValid = emailTrimmed !== "" && EMAIL_REGEX.test(emailTrimmed);

  const phoneFilled = phoneDigits.length > 0;
  const phoneValid = !phoneFilled || phoneDigits.length >= MIN_PHONE_DIGITS;

  const referrerEmailTrimmed = referrerEmail.trim();
  const referrerEmailFilled = referrerContactType === "email" && referrerEmailTrimmed !== "";
  const referrerEmailValid =
    referrerContactType !== "email" ||
    !referrerEmailFilled ||
    EMAIL_REGEX.test(referrerEmailTrimmed);

  const referrerPhoneFilled = referrerContactType === "phone" && referrerPhoneDigits.length > 0;
  const referrerPhoneValid =
    referrerContactType !== "phone" ||
    !referrerPhoneFilled ||
    referrerPhoneDigits.length >= MIN_PHONE_DIGITS;

  const isFormValid =
    studioName.trim() !== "" &&
    providerEmailValid &&
    phoneValid &&
    referrerEmailValid &&
    referrerPhoneValid;
  const canSubmit = isFormValid && !isSubmitting;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    const form = formRef.current;
    const providerPhone = phoneDigits
      ? `+49${phoneDigits.replace(/^0+/, "")}`
      : null;
    const referrerName = form?.elements?.namedItem?.("referrerName")?.value?.trim?.() || null;

    let referrerContact;
    if (referrerContactType === "email" && referrerEmailTrimmed) {
      referrerContact = referrerEmailTrimmed;
    } else if (referrerContactType === "phone" && referrerPhoneDigits) {
      referrerContact = `+49${referrerPhoneDigits.replace(/^0+/, "")}`;
    }

    const result = await submitLead({
      studioName,
      providerEmail: email,
      providerPhone,
      referrerName,
      referrerContact,
    });

    if (result?.success) {
      trackLead({
        leadType: 'provider_referral',
        source: 'recommendation_form',
      });
      setIsSubmitted(true);
    }
  };

  return (
    <div className={classes.rightSide}>
      <div className={classes.rightSideNavBarMobile}>
        <Link to="/" className={classes.logoContainer}>
          <svg xmlns="http://www.w3.org/2000/svg" width="63" height="22" viewBox="0 0 63 22" fill="none">
            <g clipPath="url(#clip0_19918_11953)">
            <path d="M11.3811 11.3499L9.72493 11.2108C5.62687 10.8791 4.2467 10.4512 4.2467 9.5203C4.2467 8.58944 5.98784 8.07586 9.5763 8.07586C13.3665 8.07586 14.8953 8.57874 15.2138 9.92688L15.2669 10.1409H19.4286L19.3862 9.84129C18.9721 6.28903 15.8083 4.56641 9.72493 4.56641C3.64154 4.56641 0.52022 6.25694 0.52022 9.5738C0.52022 12.6125 3.01515 14.0783 9.15163 14.6347L10.638 14.7738C14.7042 15.1269 16.2118 15.6618 16.2118 16.7318C16.2118 17.8981 14.3538 18.4972 10.6804 18.4972C6.51868 18.4972 4.39533 17.7162 4.19361 16.1005L4.16176 15.8651H0V16.154C0.191101 20.038 3.67339 22.0067 10.3619 22.0067C17.0505 22.0067 19.9489 20.1664 19.9489 16.5392C19.9489 13.3079 17.5495 11.8528 11.3811 11.3499Z" fill="black"/>
            <path d="M50.7583 5.10369V12.037C50.7583 15.9209 48.2209 18.3283 44.1441 18.3283C40.3115 18.3283 38.8145 16.6057 38.8145 12.1761V5.10369H25.947V0L22.1144 1.62633V16.2419C22.1144 20.2757 24.0466 21.9983 28.58 21.9983C30.1619 21.9983 31.6801 21.7308 32.9647 21.4633L33.177 21.4205V17.9859L32.8585 18.0501C31.1917 18.3925 30.4485 18.5209 29.2594 18.5209C27.0618 18.5209 25.947 17.9966 25.947 15.1185V8.31355H36.8823L34.8651 9.85429V13.4279C34.8651 19.2806 37.3282 21.9983 42.6047 21.9983C46.0127 21.9983 48.8155 20.8106 50.7583 18.5423V21.4633H54.7078V5.10369H50.7583Z" fill="black"/>
            <path d="M60.239 9.71689C61.6729 9.71689 62.8354 8.59714 62.8354 7.21586C62.8354 5.83459 61.6729 4.71484 60.239 4.71484C58.805 4.71484 57.6426 5.83459 57.6426 7.21586C57.6426 8.59714 58.805 9.71689 60.239 9.71689Z" fill="#FF003E"/>
            </g>
            <defs>
            <clipPath id="clip0_19918_11953">
            <rect width="62.8828" height="22.009" fill="white"/>
            </clipPath>
            </defs>
          </svg>
        </Link>
        <Link to="https://stu-studios.de/info/" target="_blank" rel="noopener noreferrer" className={classes.soFunktioniertStuLink}>So funktioniert Stu</Link>
      </div>
      {!isSubmitted ? (
        <>
          <div className={classes.rightSideWrapper}>
            <div className={classes.card}>
              <h2 className={`${classes.title} titleSemibold`}>Du wurdest empfohlen.</h2>
              <p className={`${classes.description} regularXl`}>
                Bitte teile uns deine Kontaktdaten mit, damit wir dich erreichen
                können.
              </p>

          <form ref={formRef} className={classes.form} onSubmit={handleSubmit}>
            <div className={classes.field}>
              <div className={classes.labelRow}>
                <label className="semiboldMiddle" htmlFor="studioName">
                  Studioname<span className={classes.requiredMark}>*</span>
                </label>
              </div>
              <input
                id="studioName"
                name="studioName"
                type="text"
                className={`${classes.input} slimRegularLargeText`}
                placeholder="Studioname eingeben"
                value={studioName}
                onChange={(event) => setStudioName(event.target.value)}
              />
            </div>

            <div className={classes.field}>
              <div className={classes.labelRow}>
                <label className="semiboldMiddle" htmlFor="email">
                  E-Mail<span className={classes.requiredMark}>*</span>
                </label>
              </div>
              <input
                id="email"
                name="email"
                type="email"
                className={`${classes.input} slimRegularLargeText`}
                placeholder="E-Mail eingeben"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>

            <div className={`${classes.field} ${classes.phoneFieldContainer}`}>
              <div className={classes.labelRow}>
                <label className="semiboldMiddle" htmlFor="phone">
                  Telefonnummer
                </label>
                <span className={`${classes.optional} semiboldMiddle`}>(optional)</span>
              </div>
              <div className={classes.phoneField}>
                <span className={`${classes.phonePrefix} ${classes.leftPhonePrefix} slimRegularLargeText`}>+49</span>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  pattern="[0-9]*"
                  className={`${classes.input} ${classes.rightPhoneInput} slimRegularLargeText`}
                  placeholder="Telefonnummer eingeben"
                  value={phoneDigits}
                  onChange={(e) => setPhoneDigits(stripNonDigits(e.target.value))}
                />
              </div>
            </div>

            <div className={classes.field}>
              <div className={classes.labelRow}>
                <label className="semiboldMiddle" htmlFor="referrerName">
                  Name des Empfehlers
                </label>
                <span className={`${classes.optional} semiboldMiddle`}>(optional)</span>
              </div>
              <input
                id="referrerName"
                name="referrerName"
                type="text"
                className={`${classes.input} slimRegularLargeText`}
                placeholder="Name des Empfehlers"
              />
            </div>

            <div className={classes.field}>
              <div className={classes.labelRow}>
                <label className="semiboldMiddle" htmlFor="referrerContact">
                  E-Mail oder Telefonnummer des Empfehlers
                </label>
                <span className={`${classes.optional} semiboldMiddle`}>(optional)</span>
              </div>
            
              <div className={classes.toggleRow}>
                <button
                  type="button"
                  className={`${classes.toggleButton} slimRegularLargeText ${
                    referrerContactType === "email" ? classes.toggleButtonActive : ""
                  }`}
                  onClick={() => setReferrerContactType("email")}
                >
                  E-Mail
                </button>
                <button
                  type="button"
                  className={`${classes.toggleButton} slimRegularLargeText ${
                    referrerContactType === "phone" ? classes.toggleButtonActive : ""
                  }`}
                  onClick={() => setReferrerContactType("phone")}
                >
                  Telefonnummer
                </button>
              </div>

              <div
                className={`${classes.referrerContactInputWrapper} ${
                  referrerContactType ? classes.referrerContactInputWrapperVisible : ""
                }`}
              >
                {referrerContactType === "email" && (
                  <input
                    id="referrerContact"
                    name="referrerContactEmail"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    className={`${classes.input} slimRegularLargeText`}
                    placeholder="E-Mail eingeben"
                    value={referrerEmail}
                    onChange={(e) => setReferrerEmail(e.target.value)}
                  />
                )}
                {referrerContactType === "phone" && (
                  <div className={classes.phoneField}>
                    <span
                      className={`${classes.phonePrefix} ${classes.leftPhonePrefix} slimRegularLargeText`}
                    >
                      +49
                    </span>
                    <input
                      id="referrerContactPhone"
                      name="referrerContactPhone"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      pattern="[0-9]*"
                      className={`${classes.input} ${classes.rightPhoneInput} slimRegularLargeText`}
                      placeholder="Telefonnummer eingeben"
                      value={referrerPhoneDigits}
                      onChange={(e) => setReferrerPhoneDigits(stripNonDigits(e.target.value))}
                    />
                  </div>
                )}
              </div>
            </div>

            <p className="slimRegularSmallText">
              Wenn du empfohlen wurdest, erhältst du und dein Empfehler die ersten
              3 Buchungen ohne Gebühr.
            </p>

          </form>

        </div>
      </div>

      <div className={classes.submitButtonContainer}>
        <button
          type="submit"
          form={undefined}
          className={`${classes.submitButton} ${
            !canSubmit ? classes.submitButtonDisabled : ""
          } ${isSubmitting ? classes.submitButtonSending : ""}`}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {isSubmitting && <span className={classes.spinner} aria-hidden="true" />}
          {isSubmitting ? "Wird gesendet..." : "Abschicken"}
        </button>
        {error && (
          <p className={`${classes.errorText} slimRegularSmallText`} role="alert">
            {error}
          </p>
        )}
        <div className={classes.textContainer}>
          <span className="slimRegularSmallText">Kein Spam. Versprochen.</span>
          <div className={classes.datenschutzBoxWrapper} ref={datenschutzRef}>
            {showDatenschutzBox && (
              <div className={classes.datenschutzBox}>
                <p className="slimRegularSmallText">
                  Wir verarbeiten deine Angaben zur Bearbeitung deiner Anfrage und zur Kontaktaufnahme. Wenn du einen Empfehler angibst, nutzen wir die angegebenen Daten nur zur internen Zuordnung der Empfehlung. Weitere Informationen findest du in unserer{" "}
                  <Link to="/datenschutz" target="_blank" rel="noopener noreferrer" className={`semiboldXs ${classes.datenschutzBoxLink}`}>
                    Datenschutzerklärung
                  </Link>
                  .
                </p>
              </div>
            )}
            <button
              type="button"
              className={`${classes.datenschutzLink} slimRegularSmallText`}
              onClick={() => setShowDatenschutzBox((prev) => !prev)}
            >
              Mehr zum Datenschutz
            </button>
          </div>
        </div>
      </div>
        </>
      ) : (
        <div className={classes.successState}>
          <div className={classes.successIconWrapper}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M5 13L9 17L19 7" stroke="#5B5FC7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className={`${classes.successTitle}`}>
            Deine Anfrage wurde erfolgreich versendet!
          </h2>
          <p className={`${classes.successText} regularXl`}>
            Wir werden dich schnellstmöglich kontaktieren.
          </p>
        </div>
      )}
      </div>
  );
};

export default RightSide;
